export type DispatchMode = "automatic" | "explicit"

interface UseDispatchModeOptions {
  /** Current LiveKit agent name backing the field. */
  agentName: string
  /**
   * The user's last dispatch-mode selection, persisted in
   * formData.metadata.livekit_dispatch_mode (see
   * getLiveKitMetadataForSave, which strips it before a save ever reaches
   * the backend). Dispatch mode is meant to stay fully derivable from
   * livekit_agent_name for anything that matters to the backend — this
   * value exists purely so an in-progress "Explicit selected, no Agent
   * Name typed yet" choice is visible to validateAgentCreate and survives
   * this component re-rendering, instead of living in a useState local to
   * this hook instance where nothing outside it (least of all form-level
   * validation) could ever see it. Undefined (e.g. a brand-new agent)
   * falls back to "automatic".
   */
  selectedMode?: DispatchMode
  /** Clears (or otherwise updates) the backing value when the mode changes. */
  onAgentNameChange: (value: string) => void
  /** Persists the user's raw selection (see `selectedMode` above). */
  onSelectedModeChange: (mode: DispatchMode) => void
}

interface UseDispatchModeResult {
  /**
   * The mode the field should render in. A non-empty agent name always
   * forces "explicit"; otherwise the last persisted selection wins, falling
   * back to "automatic".
   */
  inputMode: DispatchMode
  /** Switches mode and, when switching to "automatic", clears the agent name. */
  handleModeChange: (key: string) => void
}

/**
 * Drives the automatic-vs-explicit LiveKit dispatch-mode toggle. Purely
 * derives its rendered mode from `agentName` and `selectedMode` — an
 * existing agent name always forces "explicit"; otherwise the caller's last
 * persisted selection wins. Mirrors useConnectionMode's phone-vs-SIP
 * pattern: dispatch mode is fully derivable from whether livekit_agent_name
 * is set, as far as the backend is concerned, so nothing new needs to reach
 * the API (see getLiveKitMetadataForSave).
 *
 * Clearing the agent name when switching to "automatic" is the important
 * behavior here — it prevents a stale agent name from silently causing a
 * room_config conflict later if the user re-enables explicit mode with a
 * different name in mind.
 */
export function useDispatchMode({
  agentName,
  selectedMode,
  onAgentNameChange,
  onSelectedModeChange,
}: UseDispatchModeOptions): UseDispatchModeResult {
  const inputMode: DispatchMode = agentName
    ? "explicit"
    : (selectedMode ?? "automatic")

  const handleModeChange = (key: string) => {
    const newMode = key as DispatchMode
    onSelectedModeChange(newMode)
    if (newMode === "automatic") {
      onAgentNameChange("")
    }
  }

  return { inputMode, handleModeChange }
}
